import {
  ActionMenu,
  ConfirmationDialog,
  CopyableText,
  useNotifications
} from '@ijoin/components';
import {
  useDeleteAdminAdvisoryOrganization,
  useGetAdminAdvisoryOrganization,
  useGetAdminAdvisoryOrganizationRecordKeepingOrganizations
} from '@ijoin/hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Card,
  CardContent,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate
} from '@tanstack/react-router';

import { useToggle } from 'usehooks-ts';

import {
  BulkAdvisorUploadDialog,
  EditOrganizationDialog
} from '../../components';

const TAB_VALUES = [
  'participants',
  'advisors',
  'managed-accounts',
  'record-keepers',
  'integrations',
  'white-labels',
  'licensing',
  'activity-monitor'
];

const Layout = () => {
  const params = Route.useParams();
  const notifications = useNotifications();
  const org = useGetAdminAdvisoryOrganization(params.advisoryOrganizationId);
  const rkOrgs = useGetAdminAdvisoryOrganizationRecordKeepingOrganizations(
    params.advisoryOrganizationId
  );
  const navigate = useNavigate({ from: Route.fullPath });
  const deleteOrg = useDeleteAdminAdvisoryOrganization({
    mutation: {
      onError: () =>
        notifications.error('Failed to delete advisory organization'),
      onSuccess: () => {
        notifications.success('Advisory organization deleted');
        navigate({ to: '/organizations' });
      }
    }
  });
  const [isBulkUploadOpen, toggleBulkUploadOpen] = useToggle();
  const [isDeleteOpen, toggleDeleteOpen] = useToggle();
  const [isEditOpen, toggleEditOpen] = useToggle();
  const location = useLocation();
  const isSuperAdmin = localStorage.getItem('isAdmin') === '1';

  const onNavigateLicensing = () =>
    navigate({ to: '/organizations/$advisoryOrganizationId/licensing' });

  const linkedRkOrg = rkOrgs.data?.[0];
  const basePath = `/organizations/${params.advisoryOrganizationId}`;
  const subPath = location.pathname.slice(basePath.length);

  /* Plan detail and participant detail are standalone pages, skip org chrome */
  if (/\/plans\/[^/]+/.test(subPath) || /\/participants\/[^/]+/.test(subPath)) {
    return <Outlet />;
  }

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Link
        style={{ alignItems: 'center', display: 'flex', gap: 4 }}
        to='/organizations'>
        <ArrowBackIcon />
        Advisory Organizations
      </Link>

      {org.data && (
        <Card sx={{ p: 1 }}>
          <CardContent>
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='space-between'>
              <Stack
                alignItems='center'
                direction='row'
                spacing={3}
                sx={{ flexWrap: 'wrap' }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Name
                  </Typography>
                  <Typography variant='h5'>{org.data.name}</Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Record Keeping Org
                  </Typography>
                  <Typography variant='subtitle1'>
                    {linkedRkOrg ? (
                      <Link
                        params={{
                          recordKeepingOrganizationId:
                            linkedRkOrg.recordKeepingOrganizationId
                        }}
                        to='/recordkeepers/$recordKeepingOrganizationId'>
                        {linkedRkOrg.name}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Organization ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {params.advisoryOrganizationId.slice(0, 8)}…
                    </Typography>
                    <CopyableText value={params.advisoryOrganizationId} />
                  </Stack>
                </div>
              </Stack>
              <>
                <ActionMenu
                  actions={[
                    isSuperAdmin && {
                      content: 'Bulk Upload Advisors',
                      onSelect: toggleBulkUploadOpen
                    },
                    { content: 'Delete', onSelect: toggleDeleteOpen },
                    { content: 'Edit', onSelect: toggleEditOpen }
                  ]}
                  trigger={
                    <IconButton data-testid='actionsAdvisoryOrganization'>
                      <MoreVertIcon />
                    </IconButton>
                  }
                />
                {isBulkUploadOpen && (
                  <BulkAdvisorUploadDialog
                    advisoryOrganizationId={params.advisoryOrganizationId}
                    advisoryOrganizationName={org.data.name}
                    onClose={toggleBulkUploadOpen}
                  />
                )}
                <EditOrganizationDialog
                  advisoryOrganizationId={params.advisoryOrganizationId}
                  isOpen={isEditOpen}
                  onClose={toggleEditOpen}
                />
                <ConfirmationDialog
                  confirmationText='DELETE'
                  confirmColor='error'
                  confirmText='Delete'
                  data-testid='deleteAdvisoryOrganization'
                  isOpen={isDeleteOpen}
                  message={`Are you sure you want to delete "${org.data.name}"? This action cannot be undone.`}
                  onCancel={toggleDeleteOpen}
                  onConfirm={() => {
                    toggleDeleteOpen();
                    deleteOrg.mutate({
                      advisoryOrganizationId: params.advisoryOrganizationId
                    });
                  }}
                  title='Delete Advisory Organization'
                />
              </>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Tabs
        scrollButtons='auto'
        value={
          TAB_VALUES.find(tab =>
            location.pathname.startsWith(`${basePath}/${tab}`)
          ) ?? 'plans'
        }
        variant='scrollable'>
        <Tab
          data-testid='tabPlans'
          label='Plans'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/plans'
            })
          }
          value='plans'
        />
        <Tab
          data-testid='tabParticipants'
          label='Participants'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/participants'
            })
          }
          value='participants'
        />
        <Tab
          data-testid='tabAdvisors'
          label='Advisors'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/advisors'
            })
          }
          value='advisors'
        />
        <Tab
          data-testid='tabManagedAccounts'
          label='Managed Accounts'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/managed-accounts'
            })
          }
          value='managed-accounts'
        />
        <Tab
          data-testid='tabRecordKeepers'
          label='Record Keepers'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/record-keepers'
            })
          }
          value='record-keepers'
        />
        <Tab
          data-testid='tabIntegrations'
          label='Integrations'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/integrations'
            })
          }
          value='integrations'
        />
        <Tab
          data-testid='tabWhiteLabels'
          label='White Labels'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/white-labels'
            })
          }
          value='white-labels'
        />
        <Tab
          data-testid='tabLicensing'
          label='Licensing'
          onClick={onNavigateLicensing}
          value='licensing'
        />
        <Tab
          data-testid='tabActivityMonitor'
          label='Activity Monitor'
          onClick={() =>
            navigate({
              to: '/organizations/$advisoryOrganizationId/activity-monitor'
            })
          }
          value='activity-monitor'
        />
      </Tabs>

      <Outlet />
    </Stack>
  );
};

export const Route = createFileRoute('/organizations/$advisoryOrganizationId')({
  component: Layout
});
